<template>
  <div class="partner-school-details">
    <div
      class="partner-school-details__content"
      :class="{ 'partner-school-details__content--show-details': showDetails }">
      <div class="partner-school-details__list-container">
        <div class="partner-school-details__filters">
          <label class="partner-school-details__filter-label">
            Search
            <input
              v-model="filterText"
              class="form-control partner-school-details__filter-name"
              placeholder="Search for a partner school">
          </label>

          <b-dropdown
            class="partner-school-details__filter-type"
            menu-class="partner-school-details__filter-type-list"
            no-caret
            right>
            <template #button-content>
              Partner type
              <font-awesome-icon
                aria-hidden="true"
                icon="chevron-down"
                class="partner-school-details__filter-type-caret">
              </font-awesome-icon>
            </template>
            <b-dropdown-form>
              <label v-for="(partnerType, index) in partnerTypes"
                     :key="index">
                <font-awesome-icon
                  aria-hidden="true"
                  icon="chevron-right"
                  class="partner-school-details__filter-type-chevron">
                </font-awesome-icon>
                {{ partnerType.typeName }}
                <input v-model="partnerTypeIds"
                       :value="partnerType.nodeId"
                       type="checkbox">
                <font-awesome-icon
                  icon="check"
                  aria-hidden="true"
                  class="partner-school-details__filter-type-check">
                </font-awesome-icon>
              </label>
            </b-dropdown-form>
          </b-dropdown>
          <b-dropdown
            class="partner-school-details__filter-type"
            menu-class="partner-school-details__filter-type-list"
            no-caret
            right>
            <template #button-content>
              School type
              <font-awesome-icon
                aria-hidden="true"
                icon="chevron-down"
                class="partner-school-details__filter-type-caret">
              </font-awesome-icon>
            </template>
            <b-dropdown-form>
              <label v-for="(schoolType, index) in schoolTypes"
                     :key="index">
                <font-awesome-icon
                  aria-hidden="true"
                  icon="chevron-right"
                  class="partner-school-details__filter-type-chevron">
                </font-awesome-icon>
                {{ schoolType.label }}
                <input v-model="schoolTypeIds"
                       :value="schoolType.id"
                       type="checkbox">
                <font-awesome-icon
                  icon="check"
                  aria-hidden="true"
                  class="partner-school-details__filter-type-check">
                </font-awesome-icon>
              </label>
            </b-dropdown-form>
          </b-dropdown>
        </div>
        <ul class="partner-school-details__list">
          <li
            v-for="(school, index) in mappedSchools"
            :key="index"
            class="partner-school-details__school"
            :class="{ selected: school.schoolName === selectedSchool.schoolName }"
            @click="onSchoolClick(school)">
            <font-awesome-icon
              aria-hidden="true"
              icon="map-marker-alt"
              class="partner-school-details__icon"
              :style="{ color: '#' + school.partnerSchoolType.color }">
            </font-awesome-icon>
            <span class="partner-school-details__name">
              {{ school.schoolName }}
            </span>
          </li>
          <li v-if="noResultsFound">
            <p class="d-flex justify-content-center">
              No results found
            </p>
          </li>
        </ul>
      </div>
      <schools-map
        :schools="filteredSchools"
        class="partner-school-details__map"
        @mapped-schools="setMappedSchools"
        @marker-click="onMapSchoolClick"></schools-map>
      <div v-if="showDetails"
           class="partner-school-details__info">
        <button type="button"
                class="partner-school-details__close"
                @click="onHideDetails">
          <font-awesome-icon icon="times"
                             aria-hidden="true"></font-awesome-icon>
        </button>
        <picture v-if="selectedSchool.logo && selectedSchool.logo.url">
          <source :srcset="logo.url">
          <img :width="logo.width"
               :height="logo.height"
               class="partner-school-details__logo"
               :src="`${logo.url ||
                 '/Assets/images/placeholder.png'}${logo.url.includes( '?' ) ? '&' : '?'}format=*`"
               :alt="logo.alt">
        </picture>
        <h2 class="partner-school-details__name">
          {{ selectedSchool.schoolName }}
        </h2>

        <sanitize-html class="partner-school-details__information"
                       :dirty="selectedSchool.information">
        </sanitize-html>

        <div class="partner-school-details__type">
          <font-awesome-icon
            icon="map-marker-alt"
            aria-hidden="true"
            class="partner-school-details__icon"
            :style="{ color: '#' + selectedSchool.partnerSchoolType.color }">
          </font-awesome-icon>
          {{ selectedSchool.partnerSchoolType.typeName }}
        </div>
      </div>
    </div>
    <ul class="partner-school-details__key">
      <li class="partner-school-details__key-header">
        Key:
      </li>
      <li
        v-for="(type, index) in partnerTypes"
        :key="index"
        class="partner-school-details__key-item">
        <font-awesome-icon
          icon="map-marker-alt"
          aria-hidden="true"
          class="partner-school-details__icon"
          :style="{ color: '#' + type.color }">
        </font-awesome-icon>
        {{ type.typeName }}
      </li>
    </ul>
  </div>
</template>

<script>
import SchoolsMap from "~/Components/PartnerSchools/SchoolsMap.vue";
import SanitizeHtml from "~/Components/Base/SanitizeHtml.vue";

export default {
  name: "SchoolDetails",

  components: {
    SchoolsMap,
    SanitizeHtml,
  },

  props: {
    schools: {
      type: Array,
      default: () => [],
    },

    partnerTypes: {
      type: Array,
      default: () => [],
    },
  },

  data () {
    return {
      selectedSchool: this.blankSchool(),
      showDetails: false,
      filterText: null,
      partnerTypeIds: [],
      schoolTypeIds: [],
      mappedSchools: [],
    };
  },

  computed: {
    noResultsFound () {
      return Array.isArray( this.mappedSchools ) && this.mappedSchools.length <= 0;
    },

    schoolTypes () {
      const result = [];

      if ( this.schools && this.schools.length ) {
        this.schools.forEach( s => {
          if ( s.schoolType ) {
            const type = s.schoolType;
            if ( !result.find( x => x.label === type ) ) {
              result.push( {
                label: type,
                id: type,
              } );
            }
          }
        } );
      }

      return result;
    },

    /**
     * Filtered list of schools
     */
    filteredSchools () {
      let results = [...this.schools];

      if ( this.partnerTypeIds?.length > 0 ) {
        results = results.filter(
          ( school ) => this.partnerTypeIds.includes( school.partnerSchoolType?.nodeId ),
        );
      }

      if ( this.schoolTypeIds?.length > 0 ) {
        results = results.filter(
          ( school ) => this.schoolTypeIds.includes( school?.schoolType ),
        );
      }

      // filter any results by search term
      if ( results?.length > 0 ) {
        results = this.filterSchoolsBySearchTerm( results );
      }

      return results || [];
    },
  },

  watch: {
    filterText: {
      handler () {
        this.showDetails = false;
      },
    },
    partnerTypeIds: {
      deep: true,
      handler () {
        this.showDetails = false;
      },
    },
    schoolTypeIds: {
      deep: true,
      handler () {
        this.showDetails = false;
      },
    },
  },

  methods: {
    /**
     * Filters a list of schools by user search term
     * @param {Array} lists of schools to filter by
     */
    filterSchoolsBySearchTerm ( schools = this.schools ) {
      const filterText = this.filterText?.toUpperCase();

      return schools.filter( ( school ) => {
        let match = false;
        const schoolName = school.schoolName?.toUpperCase();
        if ( schoolName?.includes( filterText ) || !filterText ) {
          match = true;
        }
        return match;
      } );
    },
    /**
     * Sets a list of mapped schools
     * @param {Array} schools
     */
    setMappedSchools ( schools ) {
      this.mappedSchools = schools;
    },

    /**
     * When a school is clicked, load its details
     *
     * @param {object} mappedSchool The selected school
     */
    onSchoolClick ( mappedSchool ) {
      this.selectedSchool = mappedSchool;
      this.selectedSchool.panToMarker();
      this.showDetails = true;
    },

    /**
     * When a school is clicked is clicked on the map, load it as the selected school
     *
     * @param {object} school The school that was selected
     */
    onMapSchoolClick ( school ) {
      this.selectedSchool = school;
      this.showDetails = true;
    },

    /**
     * Hide the school details panel
     */
    onHideDetails () {
      this.selectedSchool = this.blankSchool();
      this.showDetails = false;
    },

    /**
     * Blank school data
     */
    blankSchool () {
      return {
        logoUrl: null,
        schoolName: null,
        information: null,
        postcode: null,
      };
    },
  },
};
</script>

<style lang="less" scoped>
.partner-school-details {
  &__content {
    display: flex;
    max-height: 700px;
    position: relative;

    &--show-details {
      height: 700px;
    }

    @media (min-width: 768px) {
      height: 700px;
    }
  }

  &__list-container {
    display: flex;
    flex-direction: column;
    flex: 0 0 100%;
    padding: 20px;
    background-color: #f2f2f2;
    overflow: hidden;

    @media (min-width: 768px) {
      flex-basis: 50%;
    }

    @media (min-width: 986px) {
      flex-basis: 33%;
    }
  }

  &__filters {
    margin-bottom: 10px;
  }

  &__map {
    flex: 1 1 auto;
    border: none;
    width: 100%;
  }

  &__info {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    overflow-y: auto;
    background-color: rgb(51, 51, 51);
    color: white;
    padding: 20px;

    .partner-school-details__close {
      position: absolute;
      top: 5px;
      right: 5px;
      border: none;
      background: none;
      color: white;
    }

    .partner-school-details__logo {
      width: 100px;
      background: transparent;
      margin-bottom: 10px;
    }

    .partner-school-details__name {
      font-size: 1.13rem;
      font-weight: 500;
      color: white;
    }

    .partner-school-details__type {
      display: flex;
      align-items: center;
      margin-top: 20px;
    }

    @media (min-width: 768px) {
      background-color: rgba(51, 51, 51, 0.8);
      width: 50%;
      left: 50%;
    }

    @media (min-width: 986px) {
      left: 33%;
      width: 33%;
    }
  }

  &__information {
    /deep/ a {
      color: #fff;
    }
  }

  &__filter-label {
    display: block;
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0;
  }

  &__filter-name {
    margin-top: 5px;
    margin-bottom: 10px;
  }

  &__filter-type {
    width: 100%;
    margin-bottom: 10px;

    /deep/ &-list {
      padding: 0;

      form {
        display: flex;
        flex-direction: column;
        padding: 0 12px;

        label {
          display: flex;
          align-items: center;
          border-bottom: solid thin #dad4ce;
          margin-bottom: 0;
          padding: 10px 0;
          cursor: pointer;

          .partner-school-details__filter-type-chevron {
            flex: 0 0 auto;
            margin-right: 10px;
          }

          .partner-school-details__filter-type-check {
            flex: 0 0 auto;
            margin-left: auto;
            padding-left: 10px;
            width: 25px;
            color: #d71920;
            font-weight: 500;
          }

          &:last-child {
            border-bottom: none;
          }

          input {
            display: none;

            & + .partner-school-details__filter-type-check {
              visibility: hidden;
            }

            &:checked + .partner-school-details__filter-type-check {
              visibility: visible;
            }
          }
        }
      }
    }

    /deep/ button.dropdown-toggle.btn.btn-secondary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: white;
      color: #7d756c;
      border: thin solid #dad4ce;
      border-radius: 4px;
      font-weight: normal;
      text-align: left;
      padding: 6px 12px;

      .partner-school-details__filter-type-caret {
        margin-left: 10px;
        padding-left: 10px;
        height: 100%;
        border-left: solid thin #dad4ce;
        flex: 0 0 auto;
        width: 25px;
      }
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    overflow-y: auto;
  }

  &__icon {
    margin-right: 10px;
    font-size: 1.25rem;
    filter: drop-shadow(1px 1px 0px #fff) drop-shadow(-1px 1px 0px #fff)
    drop-shadow(1px -1px 0px #fff) drop-shadow(-1px -1px 0px #fff);
  }

  &__school {
    cursor: pointer;
    padding: 15px;
    margin-bottom: 10px;
    background-color: #333333;
    color: white;

    &:last-child {
      margin-bottom: 0;
    }

    &.selected {
      background-color: #d71920;

      .partner-school-details__icon {
        color: white !important;
      }
    }
  }

  &__key {
    display: none;
    width: 100%;
    padding: 0;
    margin: 10px 0 0 -20px;
    list-style: none;
    justify-content: flex-end;
    flex-wrap: wrap;

    @media (min-width: 768px) {
      display: flex;
    }

    li {
      margin-left: 20px;
    }
  }

  &__key-header {
    font-weight: 500;
  }

  &__key-item {
    display: flex;
    align-items: center;
  }
}
</style>
